import { useState } from "react";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_REGEX = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

const PrivatisationForm = () => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    salon: "",
    nombrePersonnes: "",
    date: "",
    message: "",
  });

  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [isLoading, setIsLoading] = useState(false);

  const salons = [
    { id: "villejuif", name: "Salon Villejuif" },
    { id: "augrey", name: "Augrey des Couleurs" },
    { id: "ville-davray", name: "Ville d'Avray" },
  ];

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (formData.telephone.trim() && !PHONE_REGEX.test(formData.telephone)) {
      newErrors.telephone = "Format de téléphone invalide (format français)";
    }

    if (!formData.salon) {
      newErrors.salon = "Veuillez sélectionner un salon";
    }

    if (!formData.nombrePersonnes.trim()) {
      newErrors.nombrePersonnes = "Le nombre de personnes est requis";
    } else if (parseInt(formData.nombrePersonnes) < 1) {
      newErrors.nombrePersonnes =
        "Le nombre de personnes doit être supérieur à 0";
    }

    if (!formData.date.trim()) {
      newErrors.date = "La date est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/privatisation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Une erreur s'est produite");
      }

      setStatus({
        type: "success",
        message: "Votre demande de privatisation a été envoyée avec succès !",
      });
      setFormData({
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        salon: "",
        nombrePersonnes: "",
        date: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Une erreur s'est produite lors de l'envoi de la demande",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  return (
    <div
      className="max-w-8xl mx-auto w-full px-4 py-20 ph:px-24 md:flex-row md:px-48 lg:px-72 xl:px-96"
      id="privatisation-form"
    >
      <div className="flex w-full flex-col justify-between md:flex-row">
        <h2 className="mb-12 font-cormorant text-4xl text-primary ph:text-5xl sm:text-6xl lg:text-7xl">
          DEMANDE DE PRIVATISATION
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label htmlFor="nom" className="mb-2 block text-sm font-medium">
              VOTRE NOM*
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              required
              className={`w-full border ${
                errors.nom ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.nom}
              onChange={handleChange}
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-500">{errors.nom}</p>
            )}
          </div>

          <div>
            <label htmlFor="prenom" className="mb-2 block text-sm font-medium">
              VOTRE PRÉNOM*
            </label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              required
              className={`w-full border ${
                errors.prenom ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.prenom}
              onChange={handleChange}
            />
            {errors.prenom && (
              <p className="mt-1 text-sm text-red-500">{errors.prenom}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium">
              EMAIL*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className={`w-full border ${
                errors.email ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="telephone"
              className="mb-2 block text-sm font-medium"
            >
              TÉLÉPHONE*
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              className={`w-full border ${
                errors.telephone ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.telephone}
              onChange={handleChange}
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-500">{errors.telephone}</p>
            )}
          </div>

          <div>
            <label htmlFor="salon" className="mb-2 block text-sm font-medium">
              CHOISIR UN SALON*
            </label>
            <select
              id="salon"
              name="salon"
              required
              className={`w-full border ${
                errors.salon ? "border-red-500" : "border-gray-300"
              } appearance-none bg-white p-3 pr-10 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.salon}
              onChange={handleChange}
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1rem",
              }}
            >
              <option value="">Sélectionnez un salon</option>
              {salons.map((salon) => (
                <option key={salon.id} value={salon.id}>
                  {salon.name}
                </option>
              ))}
            </select>
            {errors.salon && (
              <p className="mt-1 text-sm text-red-500">{errors.salon}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="nombrePersonnes"
              className="mb-2 block text-sm font-medium"
            >
              NOMBRE DE PERSONNES*
            </label>
            <input
              type="number"
              id="nombrePersonnes"
              name="nombrePersonnes"
              min="1"
              required
              className={`w-full border ${
                errors.nombrePersonnes ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.nombrePersonnes}
              onChange={handleChange}
            />
            {errors.nombrePersonnes && (
              <p className="mt-1 text-sm text-red-500">
                {errors.nombrePersonnes}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="date" className="mb-2 block text-sm font-medium">
              DATE SOUHAITÉE*
            </label>
            <input
              type="date"
              id="date"
              name="date"
              required
              className={`w-full border ${
                errors.date ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.date}
              onChange={handleChange}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-500">{errors.date}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="mb-2 block text-sm font-medium">
            VOTRE MESSAGE
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            placeholder="Décrivez votre événement, vos besoins spécifiques et toute autre information pertinente..."
            className={`w-full border ${
              errors.message ? "border-red-500" : "border-gray-300"
            } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
            value={formData.message}
            onChange={handleChange}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-500">{errors.message}</p>
          )}
        </div>

        <div>
          {status.message && (
            <div
              className={`mb-4 rounded-md p-4 ${
                status.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {status.message}
            </div>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="group relative inline-block pb-[2px]"
          >
            <span>{isLoading ? "ENVOI EN COURS..." : "ENVOYER"}</span>
            <div className="absolute bottom-0 right-0 h-[1px] w-full bg-black">
              <div className="h-full w-0 bg-gold transition-all duration-300 ease-out group-hover:w-full"></div>
            </div>
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrivatisationForm;
